/**
 * DexLinkUndoBar — "Relevant Pokédex entries have been updated." (2026-09-02).
 *
 * The My Pokémon detail page's status toggles commit their Pokédex ticks as the
 * page LEAVES (back, a menu, or a swipe to the next Pokémon), so the confirmation
 * has to outlive the screen that caused it. Mounted globally next to
 * MyPokemonImportBar / AutoScanBar, in the same bottom slot and the same visual
 * language as the My Pokémon delete undo, which is the control it is a sibling of.
 *
 * Deliberately a bar and not a toast: the toast viewport anchors under the HEADER
 * on a phone, and this is a footnote about something that already happened, not a
 * message to read at the top of the screen.
 */
import { useEffect, useSyncExternalStore } from "react";
import { Undo2 } from "lucide-react";

import {
  clearDexLinkCommit,
  peekDexLinkCommit,
  subscribeDexLinkCommit,
  undoDexLinkCommit,
} from "@/lib/myPokemonDexLink";
import { getVariantLabel } from "@/components/dexVariantVisuals";

/** How long the undo stays on offer. Matches the My Pokémon delete undo. */
const UNDO_WINDOW_MS = 6000;

export function DexLinkUndoBar() {
  const commit = useSyncExternalStore(
    subscribeDexLinkCommit,
    peekDexLinkCommit,
    () => null,
  );

  // Keyed on the commit's timestamp: a second commit inside the window restarts
  // the clock rather than inheriting the first one's remaining time.
  const at = commit?.at ?? null;
  useEffect(() => {
    if (at == null) return;
    const timer = window.setTimeout(clearDexLinkCommit, UNDO_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [at]);

  if (!commit) return null;

  // Which dexes changed, named — "Relevant" on its own leaves the trainer to go
  // and find out. De-duplicated in the order they were written.
  const dexes = [...new Set(commit.cells.map((c) => getVariantLabel(c.variant)))];

  return (
    <div
      className="fixed left-3 right-3 z-50 rounded-xl"
      style={{
        bottom: "calc(var(--nx-safe-bottom) + 84px)",
        background: "var(--surface-dark-9)",
        border: "1px solid var(--accent-fill-glow-subtle)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
      }}
      data-testid="dex-link-undo-bar"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="flex-1 min-w-0">
          <p
            className="text-[11px] font-bold leading-snug"
            style={{ color: "var(--text-ink-bright)" }}
            data-testid="dex-link-undo-bar-text"
          >
            Relevant Pokédex entries have been updated
          </p>
          <p className="text-[10px] leading-snug" style={{ color: "var(--text-ink-faint)" }}>
            {dexes.join(" · ")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => undoDexLinkCommit()}
          data-testid="dex-link-undo"
          className="shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold inline-flex items-center gap-1.5"
          style={{
            background: "var(--accent-fill-glow-faint)",
            border: "1px solid var(--accent-fill-glow-subtle)",
            color: "var(--text-ink-bright)",
            minHeight: 36,
          }}
        >
          <Undo2 className="w-3.5 h-3.5" />
          Undo
        </button>
      </div>
    </div>
  );
}
